import { CustomAlertDialog, ErrorMessage, Spinner } from "@/components";
import OfferDialog from "@/components/formDialog/OfferDialog";
import InterviewManageDialog from "@/components/interviews/InterviewManageDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useInternshipApplicationsQuery,
  useInternshipByIdQuery,
  useUpdateApplicationStatusMutation,
} from "@/hooks/api/internship.queries";
import {
  useOffersQuery,
  useRevokeOfferMutation,
} from "@/hooks/api/offer.queries";
import usePermissions from "@/hooks/usePermissions";
import { OfferType } from "@/services/offer.service";
import { OFFER_EXTENDABLE_STATUSES, PERMISSIONS } from "@/static/permissions";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  CalendarClock,
  Clock,
  DollarSign,
  FileText,
  MapPin,
  Send,
  Undo2,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const InternshipDetailPlaceholder = () => (
  <div className="flex justify-center py-16">
    <Spinner />
  </div>
);

const InternshipsView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: internship,
    isLoading,
    isError,
  } = useInternshipByIdQuery(id || "");

  const {
    data: applicationsData,
    isLoading: isAppsLoading,
    isError: isAppsError,
  } = useInternshipApplicationsQuery(id || "", { page: 1, limit: 50 });

  const updateStatusMutation = useUpdateApplicationStatusMutation(id || "");

  const { can } = usePermissions();
  const canScheduleInterview = can(PERMISSIONS.INTERVIEW_SCHEDULE);
  const canUpdateInterview = can(PERMISSIONS.INTERVIEW_UPDATE);
  const canManageInterviews = canScheduleInterview || canUpdateInterview;
  // Deliberately NOT an interview permission — HR Executive and Technical
  // Recruiter run interviews but would get a 403 on POST /offers.
  const canExtendOffer = can(PERMISSIONS.OFFER_EXTEND);
  const canRevokeOffer = can(PERMISSIONS.OFFER_REVOKE);

  // Revoking needs the offer id, and /v1/offers has no per-application filter,
  // so pull a page and index it. If the recruiter has more offers than fit,
  // the unmatched rows simply lose their revoke button rather than showing a
  // broken one — see the note on `offersTruncated` below.
  const { data: offersData } = useOffersQuery(
    { page: 1, limit: 200 },
    canRevokeOffer
  );

  const offerByApplication = useMemo(() => {
    const map = new Map<string, OfferType>();
    offersData?.items?.forEach((offer) => map.set(offer.application_id, offer));
    return map;
  }, [offersData]);

  const offersTruncated =
    !!offersData && offersData.total > (offersData.items?.length ?? 0);

  const revokeOfferMutation = useRevokeOfferMutation(id || "");

  type SelectedApplication = { id: string; name: string };

  const [interviewTarget, setInterviewTarget] =
    useState<SelectedApplication | null>(null);
  const [offerTarget, setOfferTarget] = useState<SelectedApplication | null>(
    null
  );
  const [shortlistTarget, setShortlistTarget] =
    useState<SelectedApplication | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<
    (SelectedApplication & { offerId: string }) | null
  >(null);

  const handleStatusChange = (
    appId: string,
    appName: string,
    currentStatus: string,
    newStatus: string
  ) => {
    if (newStatus === currentStatus) return;

    // Shortlisting now sends the student a "You have been Shortlisted!" email,
    // so confirm before firing it.
    if (newStatus === "shortlisted") {
      setShortlistTarget({ id: appId, name: appName });
      return;
    }

    updateStatusMutation.mutate({
      applicationId: appId,
      status: newStatus.toLowerCase(),
    });
  };

  const confirmShortlist = () => {
    if (!shortlistTarget) return;
    updateStatusMutation.mutate({
      applicationId: shortlistTarget.id,
      status: "shortlisted",
    });
    setShortlistTarget(null);
  };

  /**
   * Only statuses a recruiter sets by hand belong here.
   * `interview_scheduled` and `interviewed` are driven by the interview
   * endpoints (schedule / mark completed) and `offer_extended` by the offer
   * endpoint — the backend sets them itself, so offering them in this dropdown
   * would desync the student's copy.
   */
  const getNextStates = (currentState: string) => {
    switch (currentState) {
      case "applied":
        return ["received", "rejected"];
      case "received":
        return ["under_review", "rejected"];
      case "under_review":
        return ["shortlisted", "rejected"];
      case "shortlisted":
      case "interview_scheduled":
      case "interviewed":
        return ["rejected"];
      default:
        // offer_extended waits on the student; offer_accepted / offer_declined
        // / rejected / withdrawn are terminal.
        return [];
    }
  };

  if (isLoading) return <InternshipDetailPlaceholder />;
  if (isError || !internship) {
    return (
      <ErrorMessage
        message="Failed to load internship details."
        onRetry={() => navigate("/activities/internships")}
      />
    );
  }

  const isRemote = internship.is_remote;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 p-4 md:p-6 lg:p-8">
      {/* Header Row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/activities/internships")}
            className="mt-1 h-9 w-9 shrink-0 rounded-xl border border-gray-200 bg-white shadow-sm hover:bg-gray-50"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </Button>

          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              {internship.title}
            </h1>
            <p className="mt-1 text-base text-gray-500">
              {internship.domain || "Not specified"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Dynamic status badge from backend */}
          {(() => {
            const s = (internship.status || "draft").toLowerCase();
            const cfg: Record<string, { label: string; className: string }> = {
              draft: {
                label: "Draft",
                className: "bg-yellow-100 text-yellow-800 border-yellow-200",
              },
              open: {
                label: "Open",
                className: "bg-gray-900 text-white border-gray-900",
              },
              closed: {
                label: "Closed",
                className: "bg-gray-200 text-gray-600 border-gray-200",
              },
            };
            const { label, className } = cfg[s] ?? {
              label: s,
              className: "bg-gray-100 text-gray-600 border-gray-200",
            };
            return (
              <Badge
                className={`border px-3 py-1 font-semibold ${className} hover:opacity-90`}
              >
                {label}
              </Badge>
            );
          })()}
          <Badge
            variant="secondary"
            className="bg-gray-900 px-3 py-1 font-semibold text-white hover:bg-gray-800"
          >
            {isRemote ? "Remote" : "On-site"}
          </Badge>
        </div>
      </div>

      {/* Top Value Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Duration */}
        <Card className="rounded-2xl border-gray-100 shadow-sm transition-shadow hover:shadow-md">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Clock size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Duration</p>
              <p className="text-lg font-bold text-gray-900">
                {internship.duration_weeks}{" "}
                {internship.duration_weeks == 1 ? "week" : "weeks"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Stipend */}
        <Card className="rounded-2xl border-gray-100 shadow-sm transition-shadow hover:shadow-md">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
              <DollarSign size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Stipend</p>
              <p className="text-lg font-bold text-gray-900">
                {internship.stipend_min != null ||
                internship.stipend_max != null ? (
                  <>
                    {internship.stipend_min != null
                      ? `₹${internship.stipend_min.toLocaleString()}`
                      : "N/A"}
                    {" - "}
                    {internship.stipend_max != null
                      ? `₹${internship.stipend_max.toLocaleString()}`
                      : "N/A"}
                  </>
                ) : (
                  "Not Disclosed"
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card className="rounded-2xl border-gray-100 shadow-sm transition-shadow hover:shadow-md">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-600">
              <MapPin size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Location</p>
              <p className="text-lg font-bold text-gray-900">
                {isRemote ? "Remote" : internship.location}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Start Date */}
        <Card className="rounded-2xl border-gray-100 shadow-sm transition-shadow hover:shadow-md">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-600">
              <Calendar size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Start Date</p>
              <p className="text-lg font-bold text-gray-900">
                {new Date(internship.start_date).toLocaleDateString([], {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Area */}
      {/* We are shifting to a single column because we don't have the right sidebar data from the screenshot */}
      <div className="grid gap-6">
        {/* Internship Details Card */}
        <Card className="overflow-hidden rounded-2xl border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-50 bg-white px-6 py-5">
            <FileText size={20} className="text-gray-700" />
            <h3 className="text-lg font-bold text-gray-900">
              Internship Details
            </h3>
          </div>

          <CardContent className="space-y-8 p-6 lg:p-8">
            {/* Description Section */}
            {internship.description && (
              <div>
                <h4 className="mb-3 text-base font-bold text-gray-900">
                  Description
                </h4>
                <p className="whitespace-pre-wrap leading-relaxed text-gray-600">
                  {internship.description}
                </p>
              </div>
            )}

            <hr className="border-gray-50" />

            {/* Quick Info Splitted Layout */}
            <div className="grid gap-8 md:grid-cols-2">
              {/* Left Column in the details block */}
              <div>
                <h4 className="mb-4 text-base font-bold text-gray-900">
                  Key Information
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-gray-50 pb-2">
                    <span className="text-sm text-gray-500">
                      Internship Type
                    </span>
                    <span className="text-sm font-semibold capitalize text-gray-900">
                      {internship.type}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-50 pb-2">
                    <span className="text-sm text-gray-500">
                      Available Openings
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {internship.openings} positions
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-50 pb-2">
                    <span className="text-sm text-gray-500">
                      Partnership Required
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {internship.requires_partnership ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column in the details block */}
              <div>
                <h4 className="mb-4 text-base font-bold text-gray-900">
                  Important Dates
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-gray-50 pb-2">
                    <span className="text-sm text-gray-500">
                      Application Deadline
                    </span>
                    <span className="text-sm font-semibold text-gray-900 text-red-600">
                      {new Date(
                        internship.application_deadline
                      ).toLocaleDateString([], {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                    <Badge
                      variant="secondary"
                      className="bg-gray-900 px-3 py-1 font-semibold text-white hover:bg-gray-800"
                    >
                      {isRemote ? "Remote" : "On-site"}
                    </Badge>
                  </div>
                  <div className="flex justify-between border-b border-gray-50 pb-2">
                    <span className="text-sm text-gray-500">
                      Expected Start Date
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {new Date(internship.start_date).toLocaleDateString([], {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Skills & Requirements Card */}
        <Card className="overflow-hidden rounded-2xl border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-50 bg-white px-6 py-5">
            <BookOpen size={20} className="text-gray-700" />
            <h3 className="text-lg font-bold text-gray-900">
              Requirements & Qualifications
            </h3>
          </div>

          <CardContent className="grid gap-8 p-6 md:grid-cols-2 lg:p-8">
            <div>
              <h4 className="mb-4 text-base font-bold text-gray-900">
                Required Skills
              </h4>
              <div className="flex flex-wrap gap-2">
                {internship.required_skills &&
                internship.required_skills.length > 0 ? (
                  internship.required_skills.map((skill: string) => (
                    <Badge
                      key={skill}
                      variant="secondary"
                      className="bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200"
                    >
                      {skill}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm italic text-gray-500">
                    No specific skills listed.
                  </span>
                )}
              </div>
            </div>

            <div>
              <h4 className="mb-4 text-base font-bold text-gray-900">
                Eligible Degrees
              </h4>
              <div className="flex flex-wrap gap-2">
                {internship.target_degree_types &&
                internship.target_degree_types.length > 0 ? (
                  internship.target_degree_types.map((deg: string) => (
                    <Badge
                      key={deg}
                      variant="outline"
                      className="border-blue-200 bg-blue-50 px-3 py-1 text-sm font-bold uppercase text-blue-700"
                    >
                      {deg}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm italic text-gray-500">
                    Open to all degrees.
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Applications Card */}
        <Card className="mt-2 overflow-hidden rounded-2xl border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-50 bg-white px-6 py-5">
            <Users size={20} className="text-gray-700" />
            <h3 className="text-lg font-bold text-gray-900">
              Student Applications
            </h3>
            {!isAppsLoading && applicationsData && (
              <Badge
                variant="secondary"
                className="ml-auto bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                {applicationsData.total || applicationsData.items?.length || 0}{" "}
                Applied
              </Badge>
            )}
          </div>

          <CardContent className="p-6 lg:p-8">
            {isAppsLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="h-6 w-6" />
              </div>
            ) : isAppsError ? (
              <div className="flex justify-center py-8">
                <p className="text-sm text-red-500">
                  Failed to load applications.
                </p>
              </div>
            ) : applicationsData?.items?.length > 0 ? (
              <>
                {offersTruncated && (
                  <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Only the most recent 200 offers were loaded, so the revoke
                    button may be missing on older ones.
                  </p>
                )}
                <div className="w-full overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
                  <Table className="w-full min-w-max text-sm">
                    <TableHeader className="border-b border-gray-100 bg-gray-50">
                      <TableRow>
                        <TableHead className="h-10 px-4 font-semibold text-gray-700">
                          Student
                        </TableHead>
                        <TableHead className="h-10 px-4 font-semibold text-gray-700">
                          Degree & Dept
                        </TableHead>
                        <TableHead className="h-10 px-4 font-semibold text-gray-700">
                          College
                        </TableHead>
                        <TableHead className="h-10 whitespace-nowrap px-4 font-semibold text-gray-700">
                          Applied On
                        </TableHead>
                        <TableHead className="h-10 px-4 font-semibold text-gray-700">
                          Resume
                        </TableHead>
                        <TableHead className="h-10 px-4 text-right font-semibold text-gray-700">
                          Action / Status
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applicationsData.items.map((app: any) => {
                        const s = (app.status || "applied").toLowerCase();
                        const cfg: Record<
                          string,
                          { label: string; className: string }
                        > = {
                          applied: {
                            label: "Applied",
                            className:
                              "bg-blue-100 text-blue-800 border-blue-200",
                          },
                          received: {
                            label: "Received",
                            className:
                              "bg-purple-100 text-purple-800 border-purple-200",
                          },
                          shortlisted: {
                            label: "Shortlisted",
                            className:
                              "bg-indigo-100 text-indigo-800 border-indigo-200",
                          },
                          under_review: {
                            label: "Under Review",
                            className:
                              "bg-amber-100 text-amber-800 border-amber-200",
                          },
                          interview_scheduled: {
                            label: "Interview Scheduled",
                            className:
                              "bg-orange-100 text-orange-800 border-orange-200",
                          },
                          interviewed: {
                            label: "Interviewed",
                            className:
                              "bg-cyan-100 text-cyan-800 border-cyan-200",
                          },
                          offer_extended: {
                            label: "Offer Extended",
                            className:
                              "bg-teal-100 text-teal-800 border-teal-200",
                          },
                          offer_accepted: {
                            label: "Offer Accepted",
                            className:
                              "bg-green-100 text-green-800 border-green-200",
                          },
                          offer_declined: {
                            label: "Offer Declined",
                            className: "bg-red-100 text-red-800 border-red-200",
                          },
                          rejected: {
                            label: "Rejected",
                            className: "bg-red-100 text-red-800 border-red-200",
                          },
                          withdrawn: {
                            label: "Withdrawn",
                            className:
                              "bg-gray-100 text-gray-800 border-gray-200",
                          },
                        };
                        const { label, className } = cfg[s] ?? {
                          label: s.replace(/_/g, " ").toUpperCase(),
                          className:
                            "bg-gray-100 text-gray-800 border-gray-200",
                        };

                        const nextStates = getNextStates(s);
                        const isTerminal = nextStates.length === 0;

                        const studentName =
                          app.student_name || `App #${app.id.slice(0, 8)}`;

                        // Interview rounds stay reachable from shortlisting all
                        // the way to the offer, so a recruiter can always mark a
                        // round completed or cancelled.
                        const showInterviews =
                          canManageInterviews &&
                          [
                            "shortlisted",
                            "interview_scheduled",
                            "interviewed",
                          ].includes(s);

                        // The backend applies no status gate on POST /offers, so
                        // this check is the only thing keeping an offer away from
                        // a rejected or withdrawn candidate. Fast-track hires are
                        // normal, so shortlisted and interview_scheduled qualify
                        // too — an interview is not a prerequisite.
                        const showExtendOffer =
                          canExtendOffer &&
                          OFFER_EXTENDABLE_STATUSES.includes(s);

                        // Revoke is the correction path for a wrong stipend, and
                        // is only possible while the offer is still `extended` —
                        // once the student answers, their decision is final.
                        const offer = offerByApplication.get(app.id);
                        const showRevokeOffer =
                          canRevokeOffer &&
                          s === "offer_extended" &&
                          offer?.status === "extended";

                        return (
                          <TableRow
                            key={app.id}
                            className="border-b border-gray-100 transition-colors hover:bg-gray-50/50"
                          >
                            <TableCell className="px-4 py-3 align-middle">
                              <div className="flex flex-col">
                                <span className="whitespace-nowrap text-[14px] font-bold text-gray-900">
                                  {studentName}
                                </span>
                                {app.roll_number && (
                                  <span className="mt-0.5 text-[12px] font-medium text-gray-500">
                                    Roll No: {app.roll_number}
                                  </span>
                                )}
                              </div>
                            </TableCell>

                            <TableCell className="px-4 py-3 align-middle">
                              <div className="flex flex-col gap-0.5">
                                {app.degree_name ? (
                                  <span className="whitespace-nowrap text-[13px] font-medium text-gray-700">
                                    {app.degree_name}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                                {app.department && (
                                  <span className="whitespace-nowrap text-[12px] text-gray-500">
                                    {app.department}
                                  </span>
                                )}
                              </div>
                            </TableCell>

                            <TableCell className="px-4 py-3 align-middle">
                              <span className="max-w-50 block truncate text-[13px] font-medium text-gray-700">
                                {app.college_name || (
                                  <span className="text-gray-400">-</span>
                                )}
                              </span>
                            </TableCell>

                            <TableCell className="whitespace-nowrap px-4 py-3 align-middle">
                              <span className="text-[13px] font-medium text-gray-600">
                                {new Date(app.applied_at).toLocaleDateString(
                                  [],
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )}
                              </span>
                            </TableCell>

                            <TableCell className="px-4 py-3 align-middle">
                              {app.resume_url ? (
                                <Button
                                  asChild
                                  variant="outline"
                                  size="sm"
                                  className="h-7 border-gray-200 bg-white px-3 text-[11px] font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                                >
                                  <a
                                    href={app.resume_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                      if (s === "applied") {
                                        updateStatusMutation.mutate({
                                          applicationId: app.id,
                                          status: "received",
                                        });
                                      }
                                    }}
                                  >
                                    View Resume
                                  </a>
                                </Button>
                              ) : (
                                <span className="text-[12px] font-medium text-gray-400">
                                  No Resume
                                </span>
                              )}
                            </TableCell>

                            <TableCell className="px-4 py-3 text-right align-middle">
                              <div className="flex items-center justify-end gap-2">
                                {showInterviews && (
                                  <Button
                                    variant="outline"
                                    className="h-7 whitespace-nowrap border-indigo-200 px-2 text-[10px] font-bold uppercase tracking-wider text-indigo-700 hover:bg-indigo-50"
                                    onClick={() =>
                                      setInterviewTarget({
                                        id: app.id,
                                        name: studentName,
                                      })
                                    }
                                  >
                                    <CalendarClock size={13} className="mr-1" />
                                    {s === "shortlisted"
                                      ? "Schedule Interview"
                                      : "Interviews"}
                                  </Button>
                                )}

                                {showExtendOffer && (
                                  <Button
                                    className="h-7 whitespace-nowrap bg-teal-600 px-2 text-[10px] font-bold uppercase tracking-wider hover:bg-teal-700"
                                    onClick={() =>
                                      setOfferTarget({
                                        id: app.id,
                                        name: studentName,
                                      })
                                    }
                                  >
                                    <Send size={13} className="mr-1" />
                                    Extend Offer
                                  </Button>
                                )}

                                {showRevokeOffer && offer && (
                                  <Button
                                    variant="outline"
                                    className="h-7 whitespace-nowrap border-amber-200 px-2 text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:bg-amber-50"
                                    disabled={revokeOfferMutation.isPending}
                                    onClick={() =>
                                      setRevokeTarget({
                                        id: app.id,
                                        name: studentName,
                                        offerId: offer.id,
                                      })
                                    }
                                  >
                                    <Undo2 size={13} className="mr-1" />
                                    Revoke Offer
                                  </Button>
                                )}

                                {isTerminal ? (
                                  <Badge
                                    className={`whitespace-nowrap border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${className}`}
                                  >
                                    {label}
                                  </Badge>
                                ) : (
                                  <Select
                                    disabled={updateStatusMutation.isPending}
                                    value={s}
                                    onValueChange={(val) =>
                                      handleStatusChange(
                                        app.id,
                                        studentName,
                                        s,
                                        val
                                      )
                                    }
                                  >
                                    <SelectTrigger
                                      className={`flex h-7 w-[130px] justify-between border bg-transparent px-2 py-0 text-left text-[10px] font-bold uppercase tracking-wider ${className}`}
                                    >
                                      <span className="truncate">{label}</span>
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={s}>{label}</SelectItem>
                                      {nextStates.map((ns) => {
                                        const nsCfg = cfg[ns] ?? {
                                          label: ns
                                            .replace(/_/g, " ")
                                            .toUpperCase(),
                                        };
                                        return (
                                          <SelectItem key={ns} value={ns}>
                                            {nsCfg.label}
                                          </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 text-gray-300">
                  <Users size={28} />
                </div>
                <h4 className="text-base font-semibold text-gray-900">
                  No applications yet
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  There are no student applications for this internship.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CustomAlertDialog
        isOpen={!!shortlistTarget}
        onOpenChange={(open) => !open && setShortlistTarget(null)}
        title="Shortlist this candidate?"
        description={`${
          shortlistTarget?.name ?? "The student"
        } will receive a "You have been Shortlisted!" email and see the update in their app.`}
        confirmText="Yes, Shortlist"
        cancelText="Not Yet"
        onConfirm={confirmShortlist}
      />

      {interviewTarget && (
        <InterviewManageDialog
          isOpen={!!interviewTarget}
          onOpenChange={(open) => !open && setInterviewTarget(null)}
          applicationId={interviewTarget.id}
          internshipId={internship.id}
          studentName={interviewTarget.name}
        />
      )}

      {offerTarget && (
        <OfferDialog
          isOpen={!!offerTarget}
          onOpenChange={(open) => !open && setOfferTarget(null)}
          applicationId={offerTarget.id}
          internshipId={internship.id}
          studentName={offerTarget.name}
        />
      )}

      <CustomAlertDialog
        isOpen={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title="Revoke this offer?"
        description={`${
          revokeTarget?.name ?? "The student"
        } will be notified and the application returns to its previous stage. You can then extend a corrected offer. This only works while the student has not yet responded.`}
        confirmText="Yes, Revoke Offer"
        cancelText="Keep Offer"
        onConfirm={() => {
          if (revokeTarget) revokeOfferMutation.mutate(revokeTarget.offerId);
          setRevokeTarget(null);
        }}
      />
    </div>
  );
};

export default InternshipsView;
